import { useState } from 'react';
import { estimateAVM } from '@/api/geo';

const TYPES_RESIDENTIELS = new Set([
  'maison', 'villa', 'chalet', 'maison_de_maitre',
  'propriete', 'maison_de_village', 'ancien_corps_de_ferme',
  'appartement', 'appartement_duplex', 'appartement_triplex',
]);

function formatEuros(value) {
  if (value == null) return '—';
  return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(value);
}

function formatEurosM2(value) {
  if (value == null) return '—';
  return `${new Intl.NumberFormat('fr-FR', { maximumFractionDigits: 0 }).format(value)} €/m²`;
}

export default function AVMEstimate({ expertiseId, typeBien, onPrefillComparaison }) {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  // Ne pas afficher pour les types non résidentiels
  if (!typeBien || !TYPES_RESIDENTIELS.has(typeBien)) {
    return null;
  }

  const handleEstimate = async () => {
    setLoading(true);
    setError(null);
    setResult(null);
    try {
      const data = await estimateAVM(expertiseId);
      setResult(data);
    } catch (err) {
      const detail = err?.response?.detail || err?.message || 'Erreur lors de l\'estimation AVM';
      setError(detail);
    } finally {
      setLoading(false);
    }
  };

  const handlePrefill = () => {
    if (!result?.estimation || !onPrefillComparaison) return;
    onPrefillComparaison({
      valeur_unitaire_m2: Math.round(result.estimation.prix_m2),
      valeur_totale: Math.round(result.estimation.valorisation_finale),
    });
  };

  const estimation = result?.estimation;
  const intervalle = estimation?.intervalle_confiance;
  const fiabilite = result?.results?.fiabilite;

  return (
    <section>
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-slate-900">Estimation AVM résidentielle</h3>
        <button
          type="button"
          onClick={handleEstimate}
          disabled={loading || !expertiseId}
          className="inline-flex items-center gap-2 rounded-lg bg-carex-600 px-4 py-2 text-sm font-medium text-white transition-colors duration-200 hover:bg-carex-700 focus:outline-none focus:ring-2 focus:ring-carex-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {loading ? (
            <>
              <svg className="h-4 w-4 animate-spin" viewBox="0 0 24 24" fill="none">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
              </svg>
              Estimation en cours…
            </>
          ) : (
            result ? 'Relancer l\'estimation' : 'Lancer l\'estimation AVM'
          )}
        </button>
      </div>

      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
          {error}
        </div>
      )}

      {estimation && (
        <div className="space-y-4">
          {/* Résultats principaux */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="rounded-lg border border-carex-200 bg-carex-50 p-4">
              <p className="text-xs font-medium text-carex-600 uppercase tracking-wide">Valorisation</p>
              <p className="mt-1 text-xl font-bold text-carex-900">
                {formatEuros(estimation.valorisation_finale)}
              </p>
            </div>
            <div className="rounded-lg border border-slate-200 bg-white p-4">
              <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">Prix / m²</p>
              <p className="mt-1 text-xl font-bold text-slate-900">
                {formatEurosM2(estimation.prix_m2)}
              </p>
            </div>
            <div className="rounded-lg border border-slate-200 bg-white p-4">
              <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">Intervalle de confiance</p>
              <p className="mt-1 text-sm font-semibold text-slate-900">
                {intervalle
                  ? `${formatEuros(intervalle.borne_inf)} — ${formatEuros(intervalle.borne_sup)}`
                  : '—'}
              </p>
            </div>
            {fiabilite && (
              <div className="rounded-lg border border-slate-200 bg-white p-4">
                <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">Fiabilité</p>
                <p className="mt-1 text-sm font-semibold text-slate-900">
                  {fiabilite.homogeneite?.appreciation || '—'}
                  {fiabilite.amplitude_confiance != null && (
                    <span className="ml-2 text-xs text-slate-500">
                      (±{fiabilite.amplitude_confiance.toFixed(1)}%)
                    </span>
                  )}
                </p>
              </div>
            )}
          </div>

          {/* Bouton pré-remplir */}
          <div>
            <button
              type="button"
              onClick={handlePrefill}
              className="inline-flex items-center gap-2 rounded-lg border border-carex-300 bg-white px-4 py-2 text-sm font-medium text-carex-700 transition-colors duration-200 hover:bg-carex-50 focus:outline-none focus:ring-2 focus:ring-carex-500 focus:ring-offset-2"
            >
              Utiliser pour la méthode par comparaison
            </button>
          </div>
        </div>
      )}
    </section>
  );
}
